import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { decodeJWT } from '../../../utils/jwt';
import { nightsBetween, formatDateSafe, todayLocalISO } from '../../../utils/dates';

const FormContainer = styled.div`
  width: 800px;
  max-width: 98vw;
  margin: 0 auto;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.10);
  padding: 32px 32px 24px 32px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow-y: auto;
  max-height: 80vh;
  @media (max-width: 900px) {
    width: 98vw;
    padding: 18px 5vw 18px 5vw;
    max-height: 96vh;
  }
`;

const Title = styled.h2`
  text-align: left;
  font-weight: 800;
  margin-bottom: 22px;
  color: #222c4a;
  letter-spacing: 0.01em;
  font-size: 1.7rem;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  color: #34574A;
  font-weight: 700;
  margin-bottom: 14px;
  margin-top: 24px;
  letter-spacing: 0.01em;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #edf2f7;
  margin: 18px 0 18px 0;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 18px;
  margin-bottom: 18px;
  align-items: flex-end;
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0;
    align-items: stretch;
  }
`;

const Field = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
`;

const Label = styled.label`
  font-size: 0.93rem;
  color: #4a5568;
  margin-bottom: 6px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 13px 14px;
  border: 1.5px solid #edf2f7;
  border-radius: 8px;
  font-size: 1rem;
  background: #f8fafc;
  margin-bottom: 0;
  transition: border .2s, box-shadow .2s;
  box-shadow: 0 1px 6px rgba(52,87,74,0.03);
  &:focus {
    border-color: #34574A;
    background: #fff;
    outline: none;
    box-shadow: 0 2px 8px rgba(52,87,74,0.08);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 13px 14px;
  border: 1.5px solid #edf2f7;
  border-radius: 8px;
  font-size: 1rem;
  background: #f8fafc;
  margin-bottom: 0;
  transition: border .2s, box-shadow .2s;
  box-shadow: 0 1px 6px rgba(52,87,74,0.03);
  appearance: none;
  min-width: 120px;
  display: block;
  &:focus {
    border-color: #34574A;
    background: #fff;
    outline: none;
    box-shadow: 0 2px 8px rgba(52,87,74,0.08);
  }
`;

const Button = styled.button`
  background: #6c63ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 16px 0;
  font-size: 1.13rem;
  font-weight: 700;
  cursor: pointer;
  margin: 20px 0 0 0;
  box-shadow: 0 2px 16px 0 rgba(76, 99, 255, 0.10);
  transition: background .2s, transform .1s, box-shadow .2s;
  letter-spacing: 0.02em;
  &:hover {
    background: #34574A;
    transform: translateY(-1px) scale(1.01);
    box-shadow: 0 4px 24px 0 rgba(76, 99, 255, 0.13);
    filter: brightness(1.07);
  }
`;

const NewReservation = ({ onSuccess }) => {
  const [form, setForm] = useState({
    hostRESERVA: '',
    usuarioRESERVA: '',
    datapublicaRESERVA: todayLocalISO(),
    deRESERVA: '',
    ateRESERVA: '',
    imovelRESERVA: '',
    statusRESERVA: 1,
    statuspagamentoRESERVA: 1,
    slugRESERVA: '0',
    whatsappRESERVA: '',
    ativoRESERVA: 1,
  });
  const [loading, setLoading] = useState(false);
  const [imoveis, setImoveis] = useState([]);
  const [loadingImoveis, setLoadingImoveis] = useState(true);
  const [reservasImovel, setReservasImovel] = useState([]);
  const [selectedDates, setSelectedDates] = useState({ start: null, end: null });
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [imovelSelecionado, setImovelSelecionado] = useState(null);

  // Pega o ID do usuário logado e define como hostRESERVA
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const payload = decodeJWT(token);
        if (payload && payload.codigo) {
          setForm(prev => ({ ...prev, hostRESERVA: String(payload.codigo) }));
        }
      } catch (e) {
        console.error('Erro ao decodificar token:', e);
      }
    }
  }, []);

  // Carrega a lista de imóveis (apenas tipo 3 - férias)
  useEffect(() => {
    const fetchImoveis = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${import.meta.env.VITE_URL_API}/imoveis?page=1&pageSize=1000`, {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          // Normaliza a resposta (pode vir como array ou objeto com data)
          const imoveisList = Array.isArray(data) ? data : (data?.data || data?.imoveis || []);
          // Filtra apenas imóveis tipo 3 (férias)
          const imoveisFerias = imoveisList.filter(imovel => 
            Number(imovel.tipoIMOVEL) === 3 || Number(imovel.tipo) === 3
          );
          setImoveis(imoveisFerias);
        }
      } catch (error) {
        console.error('Erro ao carregar imóveis:', error);
        toast.error('Erro ao carregar lista de imóveis');
      } finally {
        setLoadingImoveis(false);
      }
    };
    fetchImoveis();
  }, []);

  // Carrega reservas do imóvel selecionado
  useEffect(() => {
    if (!form.imovelRESERVA) {
      setReservasImovel([]);
      setImovelSelecionado(null);
      return;
    }
    const fetchReservasImovel = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${import.meta.env.VITE_URL_API}/imoveis/${form.imovelRESERVA}`, {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setReservasImovel(data?.reservas || []);
          setImovelSelecionado(data);
        }
      } catch (error) {
        console.error('Erro ao carregar reservas do imóvel:', error);
      }
    };
    fetchReservasImovel();
  }, [form.imovelRESERVA]);

  // Atualiza o form quando as datas são selecionadas no calendário
  useEffect(() => {
    if (selectedDates.start) {
      setForm(prev => ({ ...prev, deRESERVA: selectedDates.start }));
    }
    if (selectedDates.end) {
      setForm(prev => ({ ...prev, ateRESERVA: selectedDates.end }));
    }
  }, [selectedDates]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    
    // Se mudar o imóvel, reseta as datas selecionadas
    if (name === 'imovelRESERVA') {
      setSelectedDates({ start: null, end: null });
      setForm(prev => ({ ...prev, deRESERVA: '', ateRESERVA: '' }));
    }
  };

  // Funções auxiliares do calendário
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const formatDate = (year, month, day) => {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  };

  const isDateReserved = (dateStr) => {
    return reservasImovel.some(reserva => {
      // Apenas bloqueia se a reserva estiver Agendada (1) ou Em Andamento (2)
      // Reservas Concluídas (3) ou Canceladas (4) não bloqueiam
      if (reserva.statusRESERVA !== 1 && reserva.statusRESERVA !== 2) {
        return false;
      }
      const parseLocal = (v) => {
        if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
        const [y, m, d] = String(v).split('T')[0].split('-').map(Number);
        return new Date(y, m - 1, d);
      };
      const start = parseLocal(reserva.deRESERVA);
      const end = parseLocal(reserva.ateRESERVA);
      const [cy, cm, cd] = String(dateStr).split('-').map(Number);
      const current = new Date(cy, cm - 1, cd);
      // Regra: checkout livre - bloqueia até o dia anterior ao checkout
      const endAdj = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      endAdj.setDate(endAdj.getDate() - 1);
      return current >= start && current <= endAdj;
    });
  };

  const handleDateClick = (dateStr) => {
    if (isDateReserved(dateStr)) {
      toast.warning('Esta data já está reservada!');
      return;
    }

    if (!selectedDates.start || (selectedDates.start && selectedDates.end)) {
      // Inicia nova seleção
      setSelectedDates({ start: dateStr, end: null });
    } else {
      // Define o fim
      const start = new Date(selectedDates.start);
      const end = new Date(dateStr);
      
      if (end < start) {
        toast.warning('A data final deve ser posterior à data inicial!');
        return;
      }

      // Calcula o número de dias
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // Valida permanência mínima (pode estar em dadosBasicos ou no nível raiz)
      const permanenciaMinima = imovelSelecionado?.dadosBasicos?.permanenciaminimaIMOVEL || imovelSelecionado?.permanenciaminimaIMOVEL || 0;
      if (permanenciaMinima > 0 && diffDays < permanenciaMinima) {
        toast.warning(`A permanência mínima é de ${permanenciaMinima} dia(s)!`);
        return;
      }

      // Valida permanência máxima (pode estar em dadosBasicos ou no nível raiz)
      const permanenciaMaxima = imovelSelecionado?.dadosBasicos?.permanenciamaximaIMOVEL || imovelSelecionado?.permanenciamaximaIMOVEL || 0;
      if (permanenciaMaxima > 0 && diffDays > permanenciaMaxima) {
        toast.warning(`A permanência máxima é de ${permanenciaMaxima} dia(s)!`);
        return;
      }

      // Verifica se há datas reservadas no intervalo
      let hasReservedInRange = false;
      const current = new Date(start);
      while (current <= end) {
        const checkDate = current.toISOString().split('T')[0];
        if (isDateReserved(checkDate)) {
          hasReservedInRange = true;
          break;
        }
        current.setDate(current.getDate() + 1);
      }

      if (hasReservedInRange) {
        toast.warning('Há datas reservadas no intervalo selecionado!');
        setSelectedDates({ start: null, end: null });
        return;
      }

      setSelectedDates({ start: selectedDates.start, end: dateStr });
    }
  };

  const isDateInRange = (dateStr) => {
    if (!selectedDates.start || !selectedDates.end) return false;
    const date = new Date(dateStr);
    const start = new Date(selectedDates.start);
    const end = new Date(selectedDates.end);
    return date >= start && date <= end;
  };

  const renderCalendar = (year, month) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    const weekDays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

    const days = [];
    // Dias vazios antes do primeiro dia
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} style={{ padding: '8px' }}></div>);
    }
    // Dias do mês
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = formatDate(year, month, day);
      const isReserved = isDateReserved(dateStr);
      const isSelected = dateStr === selectedDates.start || dateStr === selectedDates.end;
      const inRange = isDateInRange(dateStr);
      const isPast = new Date(dateStr) < new Date(todayLocalISO());

      days.push(
        <div
          key={dateStr}
          onClick={() => !isPast && !isReserved && handleDateClick(dateStr)}
          style={{
            padding: '8px',
            textAlign: 'center',
            cursor: isPast || isReserved ? 'not-allowed' : 'pointer',
            backgroundColor: isReserved ? '#ffcccc' : isSelected ? '#34574A' : inRange ? '#d4edda' : isPast ? '#f0f0f0' : '#e8f5e9',
            color: isReserved ? '#999' : isSelected ? '#fff' : isPast ? '#999' : '#333',
            borderRadius: '4px',
            fontWeight: isSelected ? 'bold' : 'normal',
            opacity: isPast || isReserved ? 0.5 : 1,
          }}
        >
          {day}
        </div>
      );
    }

    return (
      <div key={`${year}-${month}`} style={{ flex: 1, minWidth: '280px' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '12px', textAlign: 'center', color: '#34574A' }}>
          {monthNames[month]} {year}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px' }}>
          {weekDays.map((day, i) => (
            <div key={i} style={{ textAlign: 'center', fontWeight: '600', fontSize: '0.85rem', color: '#666' }}>
              {day}
            </div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
          {days}
        </div>
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Valida se as datas foram selecionadas
    if (!form.deRESERVA || !form.ateRESERVA) {
      toast.error('Por favor, selecione as datas de check-in e check-out no calendário!');
      return;
    }
    
    setLoading(true);
    try {
      // Recupera token de autenticação
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('Sessão expirada. Faça login novamente.');
      }

      const nNoites = nightsBetween(form.deRESERVA, form.ateRESERVA);
      const diaria = Number(imovelSelecionado?.valorIMOVEL) || 0;
      const valorTotal = diaria > 0 && nNoites > 0 ? diaria * nNoites : null;

      const response = await fetch(`${import.meta.env.VITE_URL_API}/reservas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...form,
          hostRESERVA: Number(form.hostRESERVA),
          usuarioRESERVA: Number(form.usuarioRESERVA),
          imovelRESERVA: Number(form.imovelRESERVA),
          statusRESERVA: Number(form.statusRESERVA),
          statuspagamentoRESERVA: Number(form.statuspagamentoRESERVA),
          noitesRESERVA: nNoites,
          valorRESERVA: valorTotal,
          slugRESERVA: '0',
          ativoRESERVA: Number(form.ativoRESERVA)
        }),
      });
      if (!response.ok) {
        let detail = 'Erro ao criar reserva.';
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const data = await response.json();
            detail = data?.message || data?.error || JSON.stringify(data);
          } else {
            detail = await response.text();
          }
        } catch {
          // Ignora erros de parsing do corpo da resposta
        }
        throw new Error(detail || 'Erro ao criar reserva.');
      }
      toast.success('Reserva criada com sucesso!');
      // Mantém o hostRESERVA ao resetar o form
      const currentHost = form.hostRESERVA;
      setForm({
        hostRESERVA: currentHost, usuarioRESERVA: '',
        datapublicaRESERVA: todayLocalISO(), deRESERVA: '', ateRESERVA: '', imovelRESERVA: '', statusRESERVA: 1, statuspagamentoRESERVA: 1, slugRESERVA: '0', ativoRESERVA: 1
      });
      setSelectedDates({ start: null, end: null });
      setReservasImovel([]);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Falha ao salvar reserva:', err);
      toast.error(typeof err?.message === 'string' ? err.message : 'Erro ao criar reserva!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormContainer>
      <ToastContainer position="top-right" autoClose={4000} hideProgressBar />
      <Title>Nova Reserva</Title>
      <StyledForm onSubmit={handleSubmit}>
        <SectionTitle>Dados do Hóspede</SectionTitle>
        <FieldRow>
          <Field>
            <Label htmlFor="usuarioRESERVA">ID do Usuário (Hóspede)</Label>
            <Input
              type="number"
              name="usuarioRESERVA"
              value={form.usuarioRESERVA}
              onChange={handleChange}
              required
            />
          </Field>
          <Field>
            <Label htmlFor="whatsappRESERVA">WhatsApp do Hóspede</Label>
            <Input
              type="text"
              name="whatsappRESERVA"
              value={form.whatsappRESERVA}
              onChange={handleChange}
              placeholder="(00) 00000-0000"
            />
          </Field>
        </FieldRow>
        <Divider />
        <SectionTitle>Imóvel</SectionTitle>
        <FieldRow>
          <Field>
            <Label htmlFor="imovelRESERVA">Selecione o Imóvel</Label>
            <Select
              name="imovelRESERVA"
              value={form.imovelRESERVA}
              onChange={handleChange}
              required
              disabled={loadingImoveis}
            >
              <option value="">{loadingImoveis ? 'Carregando...' : 'Selecione um imóvel'}</option>
              {imoveis.map(imovel => (
                <option key={imovel.codigoIMOVEL || imovel.id} value={imovel.codigoIMOVEL || imovel.id}>
                  {imovel.codigoIMOVEL || imovel.id} - {imovel.tituloIMOVEL || imovel.title || 'Sem título'}
                </option>
              ))}
            </Select>
          </Field>
        </FieldRow>
        
        {/* Calendário de Disponibilidade */}
        {form.imovelRESERVA && (
          <>
            <Divider />
            <SectionTitle>Selecione as Datas</SectionTitle>
            {imovelSelecionado && (() => {
              const permMin = imovelSelecionado?.dadosBasicos?.permanenciaminimaIMOVEL || imovelSelecionado?.permanenciaminimaIMOVEL || 0;
              const permMax = imovelSelecionado?.dadosBasicos?.permanenciamaximaIMOVEL || imovelSelecionado?.permanenciamaximaIMOVEL || 0;
              return (permMin > 0 || permMax > 0) && (
                <div style={{ marginBottom: '12px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '6px', fontSize: '0.9rem', color: '#1565c0' }}>
                  {permMin > 0 && (
                    <div><strong>Permanência mínima:</strong> {permMin} dia(s)</div>
                  )}
                  {permMax > 0 && (
                    <div><strong>Permanência máxima:</strong> {permMax} dia(s)</div>
                  )}
                </div>
              );
            })()}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <button
                  type="button"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
                  style={{
                    background: '#34574A',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '1.2rem',
                  }}
                >
                  &lt;
                </button>
                <span style={{ fontWeight: '600', fontSize: '1.1rem', color: '#34574A' }}>Disponibilidade</span>
                <button
                  type="button"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                  style={{
                    background: '#34574A',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '1.2rem',
                  }}
                >
                  &gt;
                </button>
              </div>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                {renderCalendar(currentMonth.getFullYear(), currentMonth.getMonth())}
                {renderCalendar(
                  currentMonth.getMonth() === 11 ? currentMonth.getFullYear() + 1 : currentMonth.getFullYear(),
                  currentMonth.getMonth() === 11 ? 0 : currentMonth.getMonth() + 1
                )}
              </div>
              <div style={{ display: 'flex', gap: '16px', marginTop: '16px', fontSize: '0.85rem', flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '16px', height: '16px', backgroundColor: '#e8f5e9', borderRadius: '3px' }}></div>
                  Disponível
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '16px', height: '16px', backgroundColor: '#34574A', borderRadius: '3px' }}></div>
                  Selecionado
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '16px', height: '16px', backgroundColor: '#d4edda', borderRadius: '3px' }}></div>
                  No intervalo
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '16px', height: '16px', backgroundColor: '#ffcccc', borderRadius: '3px' }}></div>
                  Reservado
                </span>
              </div>
              {selectedDates.start && selectedDates.end && (
                <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#e8f5e9', borderRadius: '8px', textAlign: 'center' }}>
                  <strong>Período selecionado:</strong> {formatDateSafe(selectedDates.start)} até {formatDateSafe(selectedDates.end)}
                </div>
              )}
            </div>
          </>
        )}
        <Divider />
        <SectionTitle>Status</SectionTitle>
        <FieldRow>
          <Field>
            <Label htmlFor="statusRESERVA">Status da Reserva</Label>
            <Select name="statusRESERVA" value={form.statusRESERVA} onChange={handleChange}>
              <option value={1}>Pendente</option>
              <option value={2}>Confirmada</option>
              <option value={3}>Cancelada</option>
            </Select>
          </Field>
          <Field>
            <Label htmlFor="statuspagamentoRESERVA">Status do Pagamento</Label>
            <Select name="statuspagamentoRESERVA" value={form.statuspagamentoRESERVA} onChange={handleChange}>
              <option value={1}>Pendente</option>
              <option value={2}>Pago</option>
              <option value={3}>Cancelado</option>
            </Select>
          </Field>
          <Field>
            <Label htmlFor="ativoRESERVA">Ativo</Label>
            <Select name="ativoRESERVA" value={form.ativoRESERVA} onChange={handleChange}>
              <option value={1}>Sim</option>
              <option value={0}>Não</option>
            </Select>
          </Field>
        </FieldRow>
        <Button type="submit" disabled={loading}>{loading ? 'Salvando...' : 'Salvar Reserva'}</Button>
      </StyledForm>
    </FormContainer>
  );
};

export default NewReservation;
